import React from 'react';

import {
  ActionIcon,
  Divider,
  Group,
  Input,
  LoadingOverlay,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
  createStyles,
  px,
  Badge,
  Checkbox,
} from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import { IconAlertTriangleFilled, IconPlus } from '@tabler/icons-react';
import { AxiosError, AxiosResponse } from 'axios';
import DocumentAutoComplete, {
  DocumentAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/DocumentAutoComplete';
import PackageCategorySelect from 'components/form-fields/auto-complete-fields/PackageCategorySelect';
import AthleteStatusSelect, {
  athleteStatusOptions,
} from 'components/form-fields/select-fields/AthleteStatusSelect';
import HelpButton from 'components/HelpButton';
import WarningButton from 'components/WarningButton';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { UseMutationResult, UseQueryResult } from 'react-query';
import { ErrorMessage, Package, PackageRetrieve, PackageWrite } from 'types';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

import CreditBasedAttendanceRuleCard from './components/CreditBasedAttendanceRuleCard';
import {
  validateAthleteEndStatus,
  validateEventTypes,
  validateHasLimitedAttendanceRule,
} from './formValidators';
import { CreditBasedPackageFormValues } from './types';
import PackageDrawerState from '../../types';

const useStyles = createStyles(theme => ({
  packageForm: {
    overflowY: 'scroll',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  top: {
    display: 'grid',
    gridTemplateColumns: 'max-content auto',
    gap: theme.spacing.md,
    justifyItems: 'stretch',
    alignItems: 'start',
  },
  creditBasedInfo: {
    marginBottom: theme.spacing.md,
  },
}));

const CREDIT_BASED_LIMITED_RULE_MESSAGE =
  'Credit-based packages must have at least one limited attendance rule (credits > 0)';

const validateCreditBasedAttendanceRules = (
  values: CreditBasedPackageFormValues,
  path: string,
): string | null => {
  return (
    validateEventTypes(values, path) ??
    validateHasLimitedAttendanceRule(values, CREDIT_BASED_LIMITED_RULE_MESSAGE)
  );
};

interface ICreditBasedPackageFormProps {
  state: PackageDrawerState;
  setState: React.Dispatch<React.SetStateAction<PackageDrawerState>>;
  setPackageId: React.Dispatch<React.SetStateAction<string>>;
  formId: string;
  packageHook: UseQueryResult<{
    data: PackageRetrieve;
  }>;
  createPackageMutation: UseMutationResult<AxiosResponse<Package>, AxiosError<ErrorMessage>, PackageWrite>;
  updatePackageMutation: UseMutationResult<AxiosResponse<Package>, AxiosError<ErrorMessage>, PackageWrite>;
  permission?: boolean;
}

const CreditBasedPackageForm = ({
  state,
  setState,
  setPackageId,
  formId,
  packageHook,
  createPackageMutation,
  updatePackageMutation,
  permission = true,
}: ICreditBasedPackageFormProps): JSX.Element => {
  const { classes, theme } = useStyles();
  const staffTracking = useStaffTracking();

  const disablePackageForm = state !== 'create-package' && state !== 'edit-package';

  const form = useForm<CreditBasedPackageFormValues>({
    initialValues: {
      packageId: uuidv4(),
      name: '',
      description: '',
      packageValidityAmount: 1,
      packageValidityPeriod: 'week',
      packageCategory: '',
      autoRenew: 'Off',
      oneTimePurchase: 'Off',
      contract: {
        value: '',
        documentId: '',
        label: '',
      },
      contractOnRenew: false,
      athleteStatusOnStart: 'No status change',
      athleteStatusOnEnd: 'No status change',
      clickToCancelEnabled: 'Off',
      attendanceRules: [
        {
          attendanceRuleId: undefined,
          isInfiniteSessions: false,
          credits: 1,
          intervalCount: null,
          interval: null,
          validityIntervalCount: null,
          validityInterval: null,
          delayIntervalCount: null,
          delayInterval: null,
          kiloClassTypes: [],
          appointmentTypes: [],
          perOrPack: 'pack',
          delayStart: false,
          oldKiloClassTypeSet: new Set(),
          oldAppointmentTypeSet: new Set(),
          eventTypeState: 'same',
        },
      ],
    },
    validate: {
      name: value =>
        value.length > 0 && value.length <= 50 ? null : 'Your display name must be between 1-50 characters',
      description: value =>
        value && value.length > 250 ? 'Ensure this field has no more than 250 characters.' : null,
      autoRenew: isNotEmpty('Auto Renew cannot be empty'),
      contract: values =>
        values.label && !values.documentId ? 'Select an option or clear the field' : null,
      athleteStatusOnStart: value =>
        athleteStatusOptions.onStart.includes(value) ? null : 'Please select a valid status',
      athleteStatusOnEnd: (value, values) => {
        if (!athleteStatusOptions.onEnd.includes(value)) {
          return 'Please select a valid status';
        }
        return validateAthleteEndStatus(values);
      },
      attendanceRules: {
        kiloClassTypes: (_, values, path) => validateCreditBasedAttendanceRules(values, path),
      },
    },
    transformValues: (values: CreditBasedPackageFormValues): CreditBasedPackageFormValues => ({
      ...values,
    }),
  });

  const hasEventAdded = (): boolean => {
    if (state === 'edit-package' && packageHook.data?.data.attendanceRules) {
      return form.values.attendanceRules.some(rule => rule.eventTypeState === 'superset');
    }
    return false;
  };

  const hasAttendanceRuleChanged = (): boolean => {
    if (state === 'edit-package' && packageHook.data?.data.attendanceRules) {
      if (form.values.attendanceRules.length !== packageHook.data?.data.attendanceRules.length) {
        return true;
      }
      const arChanged = form.values.attendanceRules.some(rule => rule.eventTypeState === 'incompatible');
      if (arChanged) {
        return true;
      } else {
        return form.values.attendanceRules.some((rule, index) => {
          const originalRule = packageHook.data?.data.attendanceRules[index];
          if (!originalRule) return true; // if the original rule does not exist, it means its been removed
          return rule.credits !== originalRule.credits;
        });
      }
    }
    return false;
  };

  const handleOneTimePurchaseChange = (isOneTime: string): void => {
    form.setFieldValue('oneTimePurchase', isOneTime as 'On' | 'Off');
    if (isOneTime === 'On') {
      form.setFieldValue('autoRenew', 'Off');
    }
  };

  const handleAutoRenewChange = (isAutoRenew: string): void => {
    form.setFieldValue('autoRenew', isAutoRenew);
    if (isAutoRenew === 'On') {
      form.setValues(prev => {
        if (prev && prev.attendanceRules) {
          return {
            ...prev,
            limitQuantity: 'Off',
            setSpecificStartDate: 'Off',
            includeFollowUpPackage: false,
            attendanceRules: prev.attendanceRules.map(rule => {
              return {
                ...rule,
                delayStart: false,
                delayInterval: null,
                delayIntervalCount: null,
              };
            }),
          };
        }
        return { ...prev };
      });
    }
  };

  const handleContractChange = (item: DocumentAutoCompleteValue | null): void => {
    const newValue = {
      value: item?.value || '',
      label: item?.label || '',
      documentId: item?.documentId || '',
    };
    form.setFieldValue('contract', newValue);
    if (item === null) {
      form.setFieldValue('contractOnRenew', false);
    }
  };

  function handleClickToCancelEnabledChange(value: 'On' | 'Off'): void {
    form.setFieldValue('clickToCancelEnabled', value);
  }

  const addAttendanceRule = (): void => {
    form.insertListItem('attendanceRules', {
      isInfiniteSessions: false,
      credits: 1, // should only be null if unlimited
      intervalCount: null,
      interval: null,
      validityIntervalCount: null,
      validityInterval: null,
      delayIntervalCount: null,
      delayInterval: null,
      kiloClassTypes: [],
      appointmentTypes: [],
      perOrPack: 'pack',
      delayStart: false,
    });
  };

  const handleSubmit = (values: CreditBasedPackageFormValues): void => {
    const { successNotification, errorNotification } = notificationHandler();

    const payload: PackageWrite = {
      packageId: values.packageId,
      name: values.name,
      description: values.description,
      type: 'CREDIT_BASED' as const,
      autoRenew: values.oneTimePurchase === 'On' ? false : values.autoRenew === 'On',
      oneTimePurchase: values.oneTimePurchase === 'On',
      contract: values.contract?.documentId || null,
      contractOnRenew: values.contractOnRenew,
      packageCategory: values.packageCategory || null,
      athleteStatusOnStart:
        values.athleteStatusOnStart === 'No status change' ? null : values.athleteStatusOnStart,
      athleteStatusOnEnd:
        values.athleteStatusOnEnd === 'No status change' ? null : values.athleteStatusOnEnd,

      attendanceRules: values.attendanceRules.map(rule => ({
        attendanceRuleId: rule.attendanceRuleId ?? null,
        credits: rule.credits,
        kiloClassTypes: rule.kiloClassTypes.map(v => v.eventTypeId),
        appointmentTypes: rule.appointmentTypes.map(v => v.eventTypeId),

        interval: null,
        intervalCount: null,
        delayInterval: null,
        delayIntervalCount: null,
        validityInterval: null,
        validityIntervalCount: null,
      })),

      clickToCancelEnabled: values.clickToCancelEnabled === 'On',
      // Credit-based package disabled values
      allowProration: false,
      allowRollover: false,
      rolloverInterval: null,
      rolloverIntervalCount: null,
      rolloverLimit: null,
      successorPackage: null,
      successorBillingPlan: null,
      maxSalesAllowed: null,
      startDate: null,
      invoiceTrigger: null,
    };

    const isCreatingNewPackage = state === 'create-package' && !packageHook?.data;
    const isUpdatingExistingPackage =
      state === 'edit-package' || (state === 'create-package' && !!packageHook?.data);

    if (isCreatingNewPackage) {
      createPackageMutation.mutate(payload, {
        onError: error => {
          errorNotification('Package creation failed', error.response?.data.message);
        },
        onSuccess: response => {
          staffTracking.trackPackageCreated(
            response.data.packageId,
            response.data.name,
            response.data.type,
            values.autoRenew === 'On',
            values.oneTimePurchase === 'On',
            null,
            null,
            values.athleteStatusOnStart,
            values.athleteStatusOnEnd,
          );
          form.reset();
          setState('create-billing');
          setPackageId(response.data.packageId);
        },
      });
    }

    if (isUpdatingExistingPackage) {
      updatePackageMutation.mutate(payload, {
        onError: error => {
          errorNotification('Package update failed', error.response?.data.message);
        },
        onSuccess: response => {
          form.resetDirty();
          if (state === 'create-package') {
            setState('create-billing');
            setPackageId(response.data.packageId);
          } else {
            setState('view-package');
            successNotification('Package update successful', `${response.data.name} successfully updated.`);
          }
        },
      });
    }
  };

  useShallowEffect(() => {
    if (state !== 'create-package' && packageHook.data?.data && packageHook.isSuccess) {
      form.setValues({
        packageId: packageHook.data.data.packageId,
        name: packageHook.data.data.name,
        description: packageHook.data.data.description || '',
        autoRenew: packageHook.data.data.autoRenew ? 'On' : 'Off',
        oneTimePurchase: packageHook.data.data.oneTimePurchase ? 'On' : 'Off',
        contract: {
          value: packageHook.data.data.contract?.documentId || '',
          documentId: packageHook.data.data.contract?.documentId || '',
          label: packageHook.data.data.contract?.description || '',
        },
        contractOnRenew: packageHook.data.data.contractOnRenew,
        athleteStatusOnStart: packageHook.data.data?.athleteStatusOnStart || 'No status change',
        athleteStatusOnEnd: packageHook.data.data?.athleteStatusOnEnd || 'No status change',
        clickToCancelEnabled: packageHook.data.data.clickToCancelEnabled ? 'On' : 'Off',
        packageCategory: packageHook.data.data.packageCategory || '',
        attendanceRules: packageHook.data.data.attendanceRules.map(ar => {
          return {
            ...ar,
            attendanceRuleId: ar.attendanceRuleId,
            kiloClassTypes: ar.kiloClassTypes.map(ct => {
              return {
                eventType: 'kilo_class_type',
                eventTypeId: ct.kiloClassTypeId,
                eventTypeName: ct.name,
              };
            }),
            appointmentTypes: ar.appointmentTypes.map(ct => {
              return {
                eventType: 'appointment_type',
                eventTypeId: ct.appointmentTypeId,
                eventTypeName: ct.name,
              };
            }),
            isInfiniteSessions: ar.credits === null,
            perOrPack: 'pack',
            delayStart: ar.delayInterval !== null,
            oldKiloClassTypeSet: new Set(ar.kiloClassTypes.map(ct => ct.kiloClassTypeId)),
            oldAppointmentTypeSet: new Set(ar.appointmentTypes.map(ct => ct.appointmentTypeId)),
            eventTypeState: 'same',
          };
        }),
      });
      form.resetDirty();
    }
  }, [packageHook.data?.data, state]);

  return (
    <form id={formId} onSubmit={form.onSubmit(handleSubmit)} className={classes.packageForm}>
      <LoadingOverlay visible={state !== 'create-package' && packageHook.isLoading} />
      <Stack px={px(theme.spacing.xl) * 2} py='md' bg={disablePackageForm ? 'gray.1' : undefined}>
        <Group>
          <Title order={4}>Package Details</Title>
          {packageHook.data?.data.isArchived && (
            <Badge color='red' variant='filled' size='sm'>
              Archived
            </Badge>
          )}
        </Group>
        <div className={classes.top}>
          <Input.Label required ta='right' my='0.25rem'>
            Name
          </Input.Label>
          <TextInput
            {...form.getInputProps('name')}
            size='xs'
            required
            placeholder='Enter name'
            disabled={disablePackageForm || !permission}
            data-cy='inputNamePackage'
          />

          <Input.Label ta='right' my='0.5rem'>
            Description
          </Input.Label>

          <Textarea
            {...form.getInputProps('description')}
            maxLength={250}
            size='xs'
            placeholder='Enter description'
            disabled={disablePackageForm || !permission}
            data-cy='inputDescriptionPackage'
            error={
              !disablePackageForm && form.isDirty('description') && form.values.description.length === 250
                ? 'Maximum 250 characters reached'
                : undefined
            }
          />

          <Input.Label ta='right' my='0.25rem'>
            Package Category
          </Input.Label>
          <Group>
            <PackageCategorySelect
              {...form.getInputProps('packageCategory')}
              size='xs'
              placeholder='Select package category'
              disabled={disablePackageForm || !permission}
              // required={true}
            />
            <HelpButton width={500} position='right'>
              <Text size='sm'>To categorize this package for reporting and organizational purposes.</Text>
            </HelpButton>
          </Group>

          <Input.Label ta='right' my='0.25rem'>
            One-Time Purchase Limit
          </Input.Label>
          <Group>
            <Select
              {...form.getInputProps('oneTimePurchase')}
              size='xs'
              data={['On', 'Off']}
              disabled={disablePackageForm || !permission}
              onChange={handleOneTimePurchaseChange}
              data-cy='oneTimePurchaseOption'
            />
            <HelpButton width={500} position='right'>
              <Text size='sm'>
                Restricts this package to a single purchase per athlete. Athletes who have already purchased
                it will not see it in the app or sales portal, and staff will not see it in checkout for that
                athlete. Auto Renew is turned off and locked while this is on, since a refill would count as
                a second purchase.
              </Text>
            </HelpButton>
          </Group>

          <Input.Label ta='right' my='0.25rem'>
            Auto Renew
          </Input.Label>
          <Stack>
            <Group>
              <Select
                {...form.getInputProps('autoRenew')}
                onChange={handleAutoRenewChange}
                size='xs'
                w={220}
                required
                data={['On', 'Off']}
                disabled={disablePackageForm || form.values.oneTimePurchase === 'On' || !permission}
                data-cy='autoRenewOption'
              />
              <HelpButton width={500} position='right'>
                <Text size='sm'>
                  Auto Renew determines if this package should be automatically renewed when credits are
                  consumed.
                </Text>
              </HelpButton>
            </Group>
          </Stack>
        </div>
        <Group>
          <Input.Label>Allow Athlete Click-to-Cancel in the Athlete App</Input.Label>
          <Select
            {...form.getInputProps('clickToCancelEnabled')}
            size='xs'
            w={220}
            required
            data={['On', 'Off']}
            disabled={disablePackageForm || !permission}
            defaultValue={form.values.clickToCancelEnabled ? 'On' : 'Off'}
            data-cy='clickToCancelEnabledOption'
            onChange={handleClickToCancelEnabledChange}
          />
        </Group>
        <div className={classes.top}>
          <Input.Label ta='right' my='0.25rem'>
            Contract
          </Input.Label>
          <Group>
            <DocumentAutoComplete
              {...form.getInputProps('contract')}
              size='xs'
              placeholder='Select contract'
              type='contract'
              isTemplate
              value={form.values.contract}
              onChange={handleContractChange}
              disabled={disablePackageForm || !permission}
              data-cy='selectContractPackageForm'
            />
            <Checkbox
              {...form.getInputProps('contractOnRenew')}
              size='xs'
              label='Generate new contract upon package renew'
              checked={form.values.contractOnRenew}
              disabled={disablePackageForm || !form.values.contract.documentId || !permission}
            />
            <HelpButton width={500} position='right'>
              <Text size='sm'>
                Linking a contract template to a package will create a contract for the athlete when a
                package is applied. By default, a contract will only be generated for the first package in a
                chain of renewals. Checking the checkbox will generate a new contract each time the package
                renews.
              </Text>
            </HelpButton>
            {form.isDirty('contract') && state === 'edit-package' && (
              <WarningButton width={500} position='right'>
                <Text size='sm'>
                  Modifying the contract linked to a package will not affect packages currently attached to
                  athletes. This contract will be applied for all new packages and any renewals if contracts
                  are generated upon renewal.
                </Text>
              </WarningButton>
            )}
          </Group>

          <Input.Label ta='right' my='0.25rem'>
            Athlete Status
          </Input.Label>
          <Group>
            <Input.Label>Upon Start</Input.Label>
            <AthleteStatusSelect
              {...form.getInputProps('athleteStatusOnStart')}
              size='xs'
              disabled={disablePackageForm || !permission}
              type='onStart'
              data-cy='athleteStatusUponStart'
            />
            <Input.Label>Upon End</Input.Label>
            <AthleteStatusSelect
              {...form.getInputProps('athleteStatusOnEnd')}
              size='xs'
              disabled={disablePackageForm || !permission}
              type='onEnd'
              excludeValue={form.values.athleteStatusOnStart}
              data-cy='athleteStatusUponEnd'
            />
            <HelpButton width={500} position='right'>
              <Text size='sm'>
                These settings allow you to control what status gets applied to the athlete at the start date
                of a package and what status the athlete should transition to at the end of package. Package
                renewals will not cause status changes. Only the first package and the end of a chain will
                trigger these status changes.
                <br />
                <br />
                These settings can be overridden when applying a package to an athlete.
              </Text>
            </HelpButton>
          </Group>
        </div>
      </Stack>

      <Divider />

      <Stack
        spacing='lg'
        px={px(theme.spacing.xl) * 2}
        py='md'
        bg={disablePackageForm ? 'gray.1' : undefined}
        sx={{ flexGrow: 1 }}
      >
        <Stack spacing={0}>
          <Group>
            <Title order={4}>Attendance Rules</Title>
            {form.isDirty('attendanceRules') && state === 'edit-package' && (
              <WarningButton width={500} position='right'>
                <Stack p='sm'>
                  <Title order={5}>Warning!</Title>
                  <Text size='sm'>The attendance rules have been modified for this package.</Text>
                  <Text size='sm'>
                    These changes will not modify packages attached to athletes in the ENDED, CANCELLED,
                    ACTIVE, or HOLD states. These changes will be applied to UPCOMING packages, renewals, and
                    new packages.
                  </Text>
                  <Text size='sm'>
                    UPCOMING packages will be deleted and reapplied to reflect the new attendance rules
                    unless certain <i>*billing events</i> have occurred. Reservations linked to the upcoming
                    packages will be preserved.
                  </Text>
                  <Text size='sm'>
                    <i>*Billing events</i>: invoices generated from this package have had any attempted
                    charges
                  </Text>
                </Stack>
              </WarningButton>
            )}
          </Group>
          <Text size='sm' color='dimmed'>
            Grant access to events via rules. Credits in this pack will dispense when the package starts.
          </Text>
        </Stack>

        {hasEventAdded() && !hasAttendanceRuleChanged() && (
          <Group
            noWrap
            style={{
              outline: '2px solid #FFEA00',
              borderRadius: '10px',
              padding: '0.5rem',
              backgroundColor: '#FFFFC5',
            }}
          >
            <IconAlertTriangleFilled size={30} color='yellow' style={{ color: '#FFEA00' }} />
            <Text>
              You&apos;ve added new event types to this package. These changes will apply to all active and
              upcoming athlete packages immediately after saving.
            </Text>
          </Group>
        )}
        {hasAttendanceRuleChanged() && (
          <Group
            noWrap
            style={{
              outline: '2px solid #FFEA00',
              borderRadius: '10px',
              padding: '0.5rem',
              backgroundColor: '#FFFFC5',
            }}
          >
            <IconAlertTriangleFilled size={30} color='yellow' style={{ color: '#FFEA00' }} />
            <Text>
              Changes to credits, validity period, rollover, or attendance rules will only apply to new
              purchases or upon renewal. Active athlete packages will remain unchanged.
            </Text>
          </Group>
        )}

        {form.values.attendanceRules.map((_, index) => (
          <CreditBasedAttendanceRuleCard
            key={index}
            disabled={disablePackageForm || !permission}
            index={index}
            form={form}
          />
        ))}
        {!disablePackageForm && (
          <ActionIcon variant='subtle' color='kiloBlue' onClick={addAttendanceRule} disabled={!permission}>
            <IconPlus />
          </ActionIcon>
        )}
      </Stack>
    </form>
  );
};

export default CreditBasedPackageForm;
